// EmailMessages.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  Clock,
  Paperclip,
  Shield,
  ChevronDown,
  Calendar,
} from "lucide-react";
import api from "../../services/api";
import {
  TrendUp,
  SearchNormal,
  ExportCurve,
  ImportCurve,
  Add,
  Sort,
  Bubble,
  Setting2,
} from "iconsax-reactjs";
const EmailMessages = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  console.log("messages", messages);

  const [filters, setFilters] = useState({
    primaryIntent: "",
    priority: "",
    riskLevel: "",
    sentiment: "",
  });

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadMessages();
  }, [page, filters]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await api.getEmailMessages({
        limit,
        offset: (page - 1) * limit,
        ...filters,
      });

      setMessages(response.messages || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error("Failed to load messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await api.searchEmails(searchQuery, filters);
      setMessages(response.results || []);
      setTotal(response.count || 0);
    } catch (error) {
      console.error("Failed to search messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { bg: "bg-red-500/10", text: "text-red-400", label: "Urgent" },
      high: { bg: "bg-orange-500/10", text: "text-orange-400", label: "High" },
      normal: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Normal" },
      medium: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Medium" },
      low: { bg: "bg-green-500/10", text: "text-green-400", label: "Low" },
    };
    return config[priority] || config.medium;
  };

  const getRiskBadge = (risk) => {
    const config = {
      critical: {
        bg: "bg-red-600/10",
        text: "text-red-500",
        label: "Critical",
      },
      high: { bg: "bg-red-500/10", text: "text-red-400", label: "High Risk" },
      medium: {
        bg: "bg-orange-500/10",
        text: "text-orange-400",
        label: "Medium Risk",
      },
      low: { bg: "bg-green-500/10", text: "text-green-400", label: "Low Risk" },
    };
    return config[risk] || config.low;
  };

  const getSentimentColor = (sentiment) => {
    const colors = {
      very_positive: "text-green-400",
      positive: "text-green-500",
      neutral: "text-gray-400",
      negative: "text-red-500",
      very_negative: "text-red-400",
    };
    return colors[sentiment] || colors.neutral;
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return d.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Email Messages
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Browse and analyze your email communications
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={() => navigate("/email/legal-holds")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <Shield size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Legal Holds</span>
            </button>
            <button
              onClick={() => navigate("/email/analytics")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <TrendUp size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Email Analytics</span>
            </button>

            <button
              onClick={() => navigate("/email")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                                      0px 36px 32px -16px #7F56D936,
                                      0px 8px 16px 0px #7F56D91F,
                                      0px 2px 4px -8px #7F56D914,
                                      0px 8px 24px -16px #FFFFFF3D inset,
                                      0px -24px 32px 0px #FFFFFF38 inset,
                                      0px 0px 12px 0px #FFFFFF inset
                                    `,
                borderImageSlice: "1",
              }}
            >
              <Setting2 size={16} className="shrink-0" />
              <span className="whitespace-nowrap"> View Integration</span>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Search emails..."
                className="w-full pl-10 pr-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>

            <div className="flex flex-wrap gap-2">
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange("priority", e.target.value)}
                className="px-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                value={filters.riskLevel}
                onChange={(e) =>
                  handleFilterChange("riskLevel", e.target.value)
                }
                className="px-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Risk Levels</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                value={filters.primaryIntent}
                onChange={(e) =>
                  handleFilterChange("primaryIntent", e.target.value)
                }
                className="px-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Intents</option>
                <option value="complaint">Complaint</option>
                <option value="inquiry">Inquiry</option>
                <option value="request">Request</option>
                <option value="escalation">Escalation</option>
                <option value="sales_opportunity">Sales Opportunity</option>
                <option value="follow_up">Follow Up</option>
                <option value="negotiation">Negotiation</option>
                <option value="feedback">Feedback</option>
                <option value="cancellation">Cancellation</option>
                <option value="support">Support</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="space-y-3">
          {messages.map((message) => (
            <div
              key={message.id}
              onClick={() => navigate(`/email/messages/${message.id}`)}
              className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-white truncate text-lg">
                      {message.subject || "(No Subject)"}
                    </h3>
                    {message.hasAttachments && (
                      <Paperclip className="w-4 h-4 text-gray-400" />
                    )}
                    {!message.isRead && (
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm text-gray-400">
                      From: {message.fromName || message.fromEmail}
                    </span>
                    <span className="text-gray-600">•</span>
                    <span className="text-sm text-gray-500">
                      {formatDate(message.receivedAt)}
                    </span>
                  </div>

                  <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                    {message.bodyPreview}
                  </p>

                  {message.analysis && (
                    <div className="flex flex-wrap items-center gap-2">
                      {message.analysis.suggestedResponsePriority && (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            getPriorityBadge(
                              message.analysis.suggestedResponsePriority
                            ).bg
                          } ${
                            getPriorityBadge(
                              message.analysis.suggestedResponsePriority
                            ).text
                          }`}
                        >
                          {
                            getPriorityBadge(
                              message.analysis.suggestedResponsePriority
                            ).label
                          }
                        </span>
                      )}

                      {message.analysis.riskLevel &&
                        message.analysis.riskLevel !== "low" && (
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              getRiskBadge(message.analysis.riskLevel).bg
                            } ${getRiskBadge(message.analysis.riskLevel).text}`}
                          >
                            {getRiskBadge(message.analysis.riskLevel).label}
                          </span>
                        )}

                      {message.analysis.primaryIntent && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
                          {message.analysis.primaryIntent.replace(/_/g, " ")}
                        </span>
                      )}

                      {message.analysis.keyTopics?.slice(0, 2).map((topic) => (
                        <span
                          key={topic}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-gray-700/50 text-gray-300"
                        >
                          {topic}
                        </span>
                      ))}

                      {(message.analysis.containsPii ||
                        message.analysis.containsFinancialInfo ||
                        message.analysis.requiresRetention) && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400 flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Compliance
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {message.analysis?.sentimentScore !== undefined && (
                  <div className="ml-4 text-center">
                    <div
                      className={`text-2xl font-bold ${getSentimentColor(
                        message.analysis.sentimentLabel
                      )}`}
                    >
                      {message.analysis.sentimentScore > 0 ? "+" : ""}
                      {message.analysis.sentimentScore.toFixed(1)}
                    </div>
                    <div className="text-xs text-gray-500">Sentiment</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {messages.length === 0 && !loading && (
          <div className="text-center py-20">
            <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No messages found</p>
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between mt-8 p-6 bg-[#0C0E12] border border-gray-700 rounded-lg">
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPage(Math.min(Math.ceil(total / limit), page + 1))
                }
                disabled={page >= Math.ceil(total / limit)}
                className="px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <span className="text-gray-400 text-sm">
              Page {page} of {Math.ceil(total / limit)} • Showing{" "}
              {messages.length} of {total} messages
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailMessages;
